import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    SvgIcon,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    IconButton,
    Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WelcomeCard from "../../../BOOKLET_MODUL/Components/WelcomeCard/WelcomeCard";
import { ReactComponent as document_icon } from "../../../../Assets/Images/document_icon.svg";
import Mockexamimage from "../../../../Assets/Images/Mockexamimage.svg";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { getTopics, Subject } from "../../../../Redux/Slices/TopicSelection/TopicSelectionSlice";


const TopicSelection = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [username] = useState<string>("Tutor");

    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState<"none" | "asc" | "desc">("none");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const { data, isLoading, error: reduxError } = useAppSelector((state) => state.TopicSelectionSlice);
    const topics = data?.data || [];
    const loading = isLoading;
    const error = reduxError ? (typeof reduxError === 'string' ? reduxError : reduxError.message || "An error occurred") : null;

    const filteredTopics = topics.filter((topic: Subject) => {
        return topic.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
               (topic.description && topic.description.toLowerCase().includes(searchTerm.toLowerCase()));
    }).sort((a: Subject, b: Subject) => {
        if (sortOrder === "asc") return a.duration - b.duration;
        if (sortOrder === "desc") return b.duration - a.duration;
        return 0;
    });

    useEffect(() => {
        dispatch(getTopics());
    }, [dispatch]);

    const isNewTopic = (createdOn?: string) => {
        if (!createdOn) return false;
        const createdDate = new Date(createdOn);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - createdDate.getTime();
        const diffDays = diffTime / (1000 * 3600 * 24);
        return diffDays >= 0 && diffDays <= 14;
    };

    const handleTopicClick = (topic: Subject) => {
        navigate(`/schedule-class/sub-topic/${topic.id}`, { state: { subjectName: topic.name } });
    };

    const handleMeetClick = (e: React.MouseEvent, topic: Subject) => {
        e.stopPropagation();
        navigate("/schedule-class/calendar", {
            state: {
                subject: topic.name,
                subjectId: topic.id,
                topic: topic.name
            }
        });
    };

    // if (loading) {
    //     return (
    //         <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
    //             <CircularProgress />
    //         </Box>
    //     );
    // }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <WelcomeCard
                userName={username}
                title="Schedule Your Classes"
                description="Select a subject to view topics or schedule a meeting directly using the Meet button."
            />

            <Box sx={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center", 
                flexWrap: "wrap",
                gap: 2,
                mb: 1
            }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: "#333" }}>
                    Select Subject
                </Typography>
                
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <TextField
                        placeholder="Search subjects..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ 
                            width: { xs: "100%", sm: 250 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                bgcolor: "#fff",
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: "#999", fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <IconButton 
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{ 
                            borderRadius: "8px",
                            bgcolor: "#fff",
                            border: "1px solid #c4c4c4",
                            "&:hover": { bgcolor: "#f5f5f5" },
                            p: "8px"
                        }}
                    >
                        <FilterListIcon sx={{ color: "#999" }} />
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={() => setAnchorEl(null)}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                mt: 1.5,
                                borderRadius: '8px',
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem onClick={() => { setSortOrder("none"); setAnchorEl(null); }} selected={sortOrder === "none"}>
                            Default Order
                        </MenuItem>
                        <MenuItem onClick={() => { setSortOrder("asc"); setAnchorEl(null); }} selected={sortOrder === "asc"}>
                            Duration: Low to High
                        </MenuItem>
                        <MenuItem onClick={() => { setSortOrder("desc"); setAnchorEl(null); }} selected={sortOrder === "desc"}>
                            Duration: High to Low
                        </MenuItem>
                    </Menu>
                </Box>
            </Box>

            <Grid container spacing={2}>
                {filteredTopics.length > 0 ? (
                    filteredTopics.map((topic) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={topic.id}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: "8px",
                                    boxShadow: "0 1px 7px rgba(0, 0, 0, 0.2)",
                                    transition: "transform 0.2s",
                                    "&:hover": {
                                        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                    },
                                    cursor: "pointer",
                                }}
                                onClick={() => handleTopicClick(topic)}
                            >
                                <Box sx={{ position: "relative", height: "175px" }}>
                                    <img
                                        src={topic.image_url || Mockexamimage}
                                        alt={topic.name}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            borderRadius: "8px 8px 0 0",
                                        }}
                                    />
                                    {isNewTopic(topic.created_on) && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                top: "10px",
                                                right: "10px",
                                                background: "#22c55e",
                                                color: "#fff",
                                                px: "7px",
                                                py: "1px",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                            }}
                                        >
                                            New
                                        </Box>
                                    )}
                                </Box>

                                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "18px",
                                            mb: 1,
                                            lineHeight: 1.3,
                                            minHeight: "2.6em", // Consistent title height for 2 lines
                                        }}
                                    >
                                        {topic.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: "#4b5563",
                                            fontSize: "14px",
                                            minHeight: "4.2em", // Consistent height for 3 lines
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3, // Allow up to 3 lines
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            mb: 2,
                                        }}
                                    >
                                        {topic.description}
                                    </Typography>

                                    <Box sx={{ mt: "auto" }}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "5px", color: "#6b7280", fontSize: "15px" }}>
                                                <SvgIcon component={document_icon} inheritViewBox sx={{ fontSize: "16px", fill: "none" }} />
                                                {topic.subtopicsCount || 0} Topics
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "4px", color: "#6b7280", fontSize: "15px" }}>
                                                <AccessTimeIcon sx={{ fontSize: "15px" }} />
                                                {topic.duration} min
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                startIcon={<VideocamIcon sx={{ fontSize: "18px !important" }} />}
                                                onClick={(e) => handleMeetClick(e, topic)}
                                                sx={{
                                                    bgcolor: "#0055ff",
                                                    color: "white",
                                                    textTransform: "capitalize",
                                                    fontSize: "13px", // Reduced slightly to accommodate icon
                                                    borderRadius: "4px",
                                                    flex: 1.2, // Slightly more flex for Meet button with icon
                                                    "&:hover": { bgcolor: "#0044cc" },
                                                }}
                                            >
                                                Meet
                                            </Button>
                                            <Button
                                                fullWidth
                                                variant="outlined"
                                                endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                                                onClick={(e) => { e.stopPropagation(); handleTopicClick(topic); }}
                                                sx={{
                                                    borderColor: "#0055ff",
                                                    color: "#0055ff",
                                                    textTransform: "capitalize",
                                                    fontSize: "13px",
                                                    borderRadius: "4px",
                                                    flex: 1,
                                                    "&:hover": { 
                                                        borderColor: "#0044cc",
                                                        bgcolor: "rgba(0, 85, 255, 0.04)"
                                                    },
                                                }}
                                            >
                                                Topics
                                            </Button>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))
                ) : (
                    <Grid item xs={12}>
                        <Box sx={{ 
                            p: 8, 
                            textAlign: "center", 
                            bgcolor: "#f9fafb", 
                            borderRadius: "12px",
                            border: "1px dashed #d1d5db"
                        }}>
                            <Typography variant="h6" color="textSecondary">
                                No subjects found matching your criteria.
                            </Typography>
                            <Button 
                                variant="text" 
                                color="primary" 
                                sx={{ mt: 1 }}
                                onClick={() => {
                                    setSearchTerm("");
                                    setSortOrder("none");
                                }}
                            >
                                Clear all filters
                            </Button>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TopicSelection;

